import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Restaurant } from "@/lib/supabase";

type AppRole = "admin" | "restaurant" | "guest" | "menu_manager" | null;

async function fetchRestaurants(
  userId: string | undefined, 
  role: AppRole, 
  onlyActive: boolean = true,
  city?: string
): Promise<Restaurant[]> {
  // Public/normal users: use the restaurants_public view which excludes sensitive fields
  // This view only shows active restaurants and hides: tax_number, partner_code, company_name,
  // notes, user_id, owner_user_id, confirmation_token, registration_status, and internal date fields
  if (!userId || (role !== "restaurant" && role !== "menu_manager" && role !== "admin")) {
    let query = supabase
      .from("restaurants_public")
      .select("*")
      .eq("is_active", true); // Only show active restaurants on frontend
    if (city) query = query.eq("city", city);
    const { data, error } = await query;
    if (error) throw error;
    // Cast to Restaurant type - sensitive fields will be undefined
    return (data || []) as unknown as Restaurant[];
  }

  // Admin users: can see all restaurants with all fields (but filter by is_active if onlyActive is true)
  if (role === "admin") {
    let query = supabase.from("restaurants").select("*");
    if (onlyActive) {
      query = query.eq("is_active", true);
    }
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  // Restaurant users: only restaurants they have access to (with full details for management)
  if (role === "restaurant") {
    const { data: assigned } = await supabase
      .from("restaurant_users")
      .select("restaurant_id")
      .eq("user_id", userId);

    const assignedIds = assigned?.map((r) => r.restaurant_id) || [];

    const { data: owned } = await supabase
      .from("restaurants")
      .select("id")
      .eq("user_id", userId);

    const ownedIds = owned?.map((r) => r.id) || [];
    const restaurantIds = Array.from(new Set([...assignedIds, ...ownedIds]));

    if (restaurantIds.length === 0) {
      return [];
    }

    let query = supabase
      .from("restaurants")
      .select("*")
      .in("id", restaurantIds);
    
    if (onlyActive) {
      query = query.eq("is_active", true);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  // Menu manager users: only restaurants they manage (with full details for menu editing)
  if (role === "menu_manager") {
    const { data: assigned } = await supabase
      .from("menu_manager_restaurants")
      .select("restaurant_id")
      .eq("user_id", userId);

    const restaurantIds = assigned?.map((r) => r.restaurant_id) || [];

    if (restaurantIds.length === 0) {
      return [];
    }

    let query = supabase
      .from("restaurants")
      .select("*")
      .in("id", restaurantIds);
    
    if (onlyActive) {
      query = query.eq("is_active", true);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  return [];
}

export function useRestaurants(
  userId: string | undefined, 
  role: AppRole, 
  enabled: boolean = true,
  onlyActive: boolean = true,
  city?: string
) {
  return useQuery({
    queryKey: ["restaurants", userId, role, onlyActive ? "active_only" : "all", city ?? ""],
    queryFn: () => fetchRestaurants(userId, role, onlyActive, city),
    enabled: enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}
